import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "../styles/BookMoviePage.scss";
import axiosInstance from "../utils/axios";
import { to12HourFormat } from "../utils/dateFormats";
import { useNavigate } from "react-router-dom";
import ThirdPartyTheaters from "../components/ThirdPartyTheaters";


const BookMoviePage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(false);
  const [shows, setShows] = useState([]);
  const [movie, setMovie] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchShows();
    fetchMovie();
    // eslint-disable-next-line
  }, []);

  const fetchShows = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `show-management/movies/${slug}/shows/`
      );
      
      // First group by theatre
      const groupedByTheatre = res.data.reduce((acc, show) => {
        const theatreId = show.theatre;
        if (!acc[theatreId]) {
          acc[theatreId] = {
            theatre_id: show.theatre,
            theatre_name: show.theatre_name,
            logo: show.logo,
            screens: {}
          };
        }
        
        // Then group screens within each theatre
        const screenKey = show.screen || show.id;
        if (!acc[theatreId].screens[screenKey]) {
          acc[theatreId].screens[screenKey] = {
            screen: show.screen,
            screen_name: show.screen_name,
            movie: show.movie,
            movie_name: show.movie_name,
            shows: [] // Store multiple shows per screen
          };
        }
        
        // Add the entire show object with its unique ID and dates
        acc[theatreId].screens[screenKey].shows.push({
          id: show.id, // Preserve the unique show ID (pk)
          dates: show.dates || []
        });
        
        return acc;
      }, {});
      
      // Convert to array format
      const formattedShows = Object.values(groupedByTheatre).map(theatre => ({
        ...theatre,
        screens: Object.values(theatre.screens).map(screen => ({
          ...screen,
          // Consolidate dates from all shows on this screen
          dates: consolidateDatesWithShowIds(screen.shows)
        }))
      }));
      
      // Sort theatres alphabetically by name
      formattedShows.sort((a, b) => 
        a.theatre_name.localeCompare(b.theatre_name)
      );
      
      setShows(formattedShows);
    } catch (err) {
      // console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Consolidate dates and preserve show IDs for each timeslot
  const consolidateDatesWithShowIds = (shows) => {
    const dateMap = {};
    
    shows.forEach(show => {
      show.dates.forEach(dateObj => {
        const dateKey = dateObj.date;
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            id: dateObj.id,
            date: dateObj.date,
            timeslots: []
          };
        }
        
        // Add timeslots with their associated show ID
        if (dateObj.timeslots) {
          dateObj.timeslots.forEach(slot => {
            // Check if this exact timeslot already exists
            const exists = dateMap[dateKey].timeslots.some(s => s.id === slot.id);
            if (!exists) {
              dateMap[dateKey].timeslots.push({
                ...slot,
                show_id: show.id // Attach the show ID to each timeslot
              });
            }
          });
        }
      });
    });
    
    // Sort timeslots by start time
    Object.values(dateMap).forEach(dateObj => {
      dateObj.timeslots.sort((a, b) => {
        return a.timeslot_start.localeCompare(b.timeslot_start);
      });
    });
    
    return Object.values(dateMap);
  };

  const fetchMovie = async () => {
    try {
      const res = await axiosInstance.get(`movie/movie/${slug}`);
      setMovie(res.data);
    } catch (err) {
      // console.log(err);
    }
  };

  const formatShowDate = (dateStr) => {
    const date = new Date(dateStr);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() === today.getTime()) {
      return 'Today';
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    }

    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  };

  // Check if a timeslot is a premiere show
  const isPremiereShow = (slotTitle) => {
    return slotTitle && slotTitle.toLowerCase() === 'premier show';
  };

  const gotoSeat = (theatre_id, screen_id, movie_id, 
    date_id, slot_id, pk, name, slot_name,
    theatre_name, screen_name
  ) => {
    sessionStorage.setItem("bookingContext", JSON.stringify({
        state: {
          theatre_id,
          screen_id,
          movie_id,
          date_id,
          slot_id,
          pk,
          name,
          slot_name,
          theatre_name,
          screen_name        
        },
      }));
    navigate("/book/seat", {
        state: {
          theatre_id,
          screen_id,
          movie_id,
          date_id,
          slot_id,
          pk,
          name,
          slot_name,
          theatre_name,
          screen_name        
        },
      });
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "200px" }}>Loading...</div>
    );
  }

  return (
    <div className="book-movie-layout">
      <div className="book-movie container py-4">
        <h2 className="mb-4">
          {movie.name} ({movie.display_language})
        </h2>

        {shows.length <= 0 ? (
           <div>
            {/* <p>Sorry, no shows are available at this time.</p> */}
          </div> 
        ) : (
          <div className="theatres-container">
            {shows.map((theatre) => (
              <div key={theatre.theatre_id} className="theatre-card">
                {/* Theatre Header */}
                <div className="theatre-header">
                  <div className="theatre-info">
                    {theatre.logo ? (
                      <img
                        src={`${process.env.REACT_APP_MEDIA_URL}${theatre.logo}`}
                        alt={theatre.theatre_name}
                        className="theatre-logo"
                      />
                    ) : (
                      <div className="theatre-icon">🎬</div>
                    )}
                    <h4 className="theatre-name">{theatre.theatre_name}</h4>
                  </div>
                </div>

                {/* Screens */}
                <div className="screens-container">
                  {theatre.screens && theatre.screens.map((screen) => (
                    <div key={screen.screen} className="screen-section">
                      <div className="screen-header">
                        <h5 className="screen-name">{screen.screen_name}</h5>
                      </div>

                      {/* Dates and Timeslots */}
                      <div className="dates-container">
                        {screen.dates
                          .filter((dateObj) => dateObj.timeslots.some((slot) => slot.status === true))
                          .map((dateObj) => {
                            const showDate = new Date(dateObj.date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            showDate.setHours(0, 0, 0, 0);

                            if (showDate < today) return null;

                            const isToday = showDate.getTime() === today.getTime();

                            return (
                              <div key={dateObj.id} className="date-section">
                                <div className="date-label">{formatShowDate(dateObj.date)}</div>

                                <div className="timeslots">
                                  {dateObj.timeslots
                                    .filter((slot) => {
                                      if (!slot.status) return false;

                                      if (isToday) {
                                        const now = new Date();
                                        const [hours, minutes] = slot.timeslot_start.split(':');
                                        const slotTime = new Date();
                                        slotTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                        return slotTime >= now;
                                      }

                                      return true;
                                    })
                                    .map((slot) => {
                                      const isPremiere = isPremiereShow(slot.timeslot_title);
                                      
                                      return (
                                        <button
                                          key={slot.id}
                                          type="button"
                                          className={`timeslot-btn ${isPremiere ? 'premiere-slot' : ''}`}
                                          onClick={() => gotoSeat(
                                            theatre.theatre_id, 
                                            screen.screen, 
                                            screen.movie,
                                            dateObj.id, 
                                            slot.id, 
                                            slot.show_id, // Use the preserved show_id
                                            screen.movie_name, 
                                            slot.timeslot_title,
                                            theatre.theatre_name, 
                                            screen.screen_name
                                          )}
                                        >
                                          {isPremiere ? (
                                            <div className="premiere-content">
                                              <span className="slot-title premiere-title">
                                                {slot.timeslot_title}
                                              </span>
                                              <span className="slot-time premiere-time">
                                                {to12HourFormat(slot.timeslot_start)}
                                              </span>
                                            </div>
                                          ) : (
                                            <span className="slot-time">
                                              {to12HourFormat(slot.timeslot_start)}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                </div>
                              </div>
                            );
                          })
                          .filter(Boolean)
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Third Party Theatres */}
        <ThirdPartyTheaters theaters={movie.third_party_booking_links} />
      </div>
    </div>
  );
};

export default BookMoviePage;